import gsap from 'gsap';
import { useCallback, useEffect, useImperativeHandle, useRef, type RefObject } from 'react';
import type { EconomyNaming, PublicWheelSegment } from '@shared';
import { Icon, type IconName } from '../ui/Icon';
import {
  CENTER,
  INNER_RADIUS,
  OUTER_RADIUS,
  VIEWBOX,
  fitFontSize,
  labelScale,
  pegsCrossed,
  polar,
  rotationForTarget,
  type Slice,
} from './geometry';
import { sfx } from '../../lib/audio';
import { creditWord } from '../../lib/format';
import styles from './WheelStage.module.css';

/** Full revolutions before the wheel starts hunting for its target. */
const SPIN_TURNS = 6;
const SPIN_DURATION = 5.2;
/** Degrees the wheel loads up backwards before launching. */
const ANTICIPATION = 9;
/** Degrees it overshoots before springing back onto the exact target. */
const OVERSHOOT = 2;
/** Ticks closer together than this are dropped — past here it's just noise. */
const MIN_TICK_INTERVAL_MS = 26;
/** Label sizes in viewBox units; both shrink rather than overflow the slice. */
const PRIMARY_SIZE = 6.4;
const PRIMARY_MIN_SIZE = 3.7;
const SECONDARY_SIZE = 3.3;
const SECONDARY_MIN_SIZE = 2.5;

/**
 * The house names, for a caller that has no view to read them from.
 *
 * The stage is also mounted outside the player's game — the studio drives one
 * for filming — so the naming is optional rather than required, and defaults to
 * what a creator who has renamed nothing would have sent.
 */
const DEFAULT_NAMING: EconomyNaming = {
  creditName: 'Credit',
  creditNamePlural: 'Credits',
  xpName: 'XP',
};

export interface WheelStageHandle {
  /** Spins to park `targetMid` under the pointer. Resolves once fully settled. */
  spinTo(targetMid: number): Promise<void>;
  /** Cancels any running animation and silences the ticks. */
  cancel(): void;
}

interface WheelStageProps {
  ref?: RefObject<WheelStageHandle | null>;
  slices: Slice[];
  hubIcon: IconName;
  /** Rendered inside the hub under the icon — the cost, or "FREE". */
  hubLabel: string;
  /** The creator's words for her money. Defaults to the house ones. */
  naming?: EconomyNaming;
  spinning: boolean;
  disabled: boolean;
  onSpin: () => void;
  reducedMotion: boolean;
}

/**
 * The wheel itself: a rotating SVG under a fixed lighting overlay.
 *
 * The split matters. Slices, pegs and labels live on the rotor and turn with the
 * wheel; the specular highlight, vignette, rim and pointer are painted on top and
 * never move. Light that stays put while the wheel spins underneath is most of
 * what sells the "real object" impression without a single 3D asset.
 *
 * Only `transform` is animated, on one composited layer, so a spin stays on the
 * GPU at 60 FPS regardless of how many slices are on the wheel.
 */
export function WheelStage({
  ref,
  slices,
  hubIcon,
  hubLabel,
  naming = DEFAULT_NAMING,
  spinning,
  disabled,
  onSpin,
  reducedMotion,
}: WheelStageProps) {
  const rotorRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef<HTMLDivElement>(null);
  const rotation = useRef(0);
  const timeline = useRef<gsap.core.Timeline | null>(null);
  const lastTickAt = useRef(0);
  /** Settles a pending `spinTo` when a spin is cancelled instead of completed. */
  const pendingResolve = useRef<(() => void) | null>(null);

  const pegCount = slices.length;

  const applyRotation = useCallback((value: number) => {
    rotation.current = value;
    if (rotorRef.current) rotorRef.current.style.transform = `rotate(${value}deg)`;
  }, []);

  /** Pointer kick + click, scaled by how fast the wheel is going. */
  const handlePegs = useCallback((count: number, speedDegPerSec: number) => {
    if (count <= 0) return;

    const pointer = pointerRef.current;
    if (pointer) {
      const intensity = gsap.utils.clamp(0.25, 1, speedDegPerSec / 900);
      gsap.fromTo(
        pointer,
        { rotate: -14 * intensity },
        { rotate: 0, duration: 0.32, ease: 'elastic.out(1, 0.32)', overwrite: true },
      );
    }

    const now = performance.now();
    if (now - lastTickAt.current < MIN_TICK_INTERVAL_MS) return;
    lastTickAt.current = now;

    // Faster wheel → brighter, louder click. It reads as momentum.
    const speed = gsap.utils.clamp(0, 1, speedDegPerSec / 1600);
    sfx.play('tick', { rate: 0.92 + speed * 0.5, volume: 0.45 + speed * 0.55 });
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      cancel() {
        timeline.current?.kill();
        timeline.current = null;
        // Killing a timeline skips its onComplete, so release the awaiter here
        // or `handleSpin` would hang forever on an interrupted spin.
        pendingResolve.current?.();
        pendingResolve.current = null;
      },

      spinTo(targetMid: number) {
        timeline.current?.kill();
        pendingResolve.current?.();

        const from = rotation.current;
        const target = rotationForTarget(from, targetMid, reducedMotion ? 0 : SPIN_TURNS);

        return new Promise<void>((resolve) => {
          pendingResolve.current = resolve;
          const settle = () => {
            pendingResolve.current = null;
            resolve();
          };

          if (reducedMotion) {
            // No spectacle, but still land on the right slice.
            timeline.current = gsap.timeline({ onComplete: settle }).to(
              { value: from },
              {
                value: target,
                duration: 0.45,
                ease: 'power2.out',
                onUpdate() {
                  applyRotation(this.targets()[0].value as number);
                },
              },
            );
            return;
          }

          const proxy = { value: from };
          let previous = from;
          let previousAt = performance.now();

          sfx.play('launch');

          timeline.current = gsap
            .timeline({ onComplete: settle })
            // Load up backwards: the pause before the launch is what makes the
            // launch feel like force rather than a jump cut.
            .to(proxy, {
              value: from - ANTICIPATION,
              duration: 0.3,
              ease: 'power2.inOut',
              onUpdate() {
                applyRotation(proxy.value);
              },
            })
            .to(proxy, {
              value: target + OVERSHOOT,
              duration: SPIN_DURATION,
              ease: 'power4.out',
              // Rotation is written first, before anything that could fail:
              // a broken tick must never be able to freeze the wheel.
              onUpdate: () => {
                applyRotation(proxy.value);

                // Angular speed from wall-clock time. Deriving it from GSAP's
                // own timing would mean reaching into the tween via `this`,
                // which is not the tween inside a timeline child.
                const now = performance.now();
                const elapsed = Math.max(1, now - previousAt);
                const speed = (Math.abs(proxy.value - previous) / elapsed) * 1000;

                handlePegs(pegsCrossed(previous, proxy.value, pegCount), speed);
                previous = proxy.value;
                previousAt = now;
              },
            })
            // The pointer holds the last peg, then the wheel rocks back onto it.
            .to(proxy, {
              value: target,
              duration: 0.9,
              ease: 'elastic.out(1, 0.45)',
              onUpdate() {
                applyRotation(proxy.value);
              },
            });
        });
      },
    }),
    [applyRotation, handlePegs, pegCount, reducedMotion],
  );

  // Park the wheel with the first prize centred under the pointer. At rotation
  // 0 the pointer straddles a peg, which reads as "between prizes".
  const parked = useRef(false);
  useEffect(() => {
    if (parked.current || slices.length === 0) return;
    parked.current = true;
    applyRotation(-slices[0]!.mid);
  }, [slices, applyRotation]);

  useEffect(
    () => () => {
      timeline.current?.kill();
    },
    [],
  );

  const pegRadius = OUTER_RADIUS + 2.6;

  return (
    <div className={styles.stage}>
      {/* The creator's world, behind everything. Painted in gradients so it
          costs no asset and no request — an artwork file can replace
          `--wheel-scene` later without this component changing. */}
      <div className={styles.scene} aria-hidden="true" />
      <div className={styles.ambient} aria-hidden="true" />

      <div className={styles.rotor} ref={rotorRef} aria-hidden="true">
        <svg viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`} className={styles.wheelSvg}>
          <defs>
            {/* Warms the outer half of every slice so the wheel reads as dished. */}
            <radialGradient id="sliceDepth" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#000" stopOpacity="0.55" />
              <stop offset="42%" stopColor="#000" stopOpacity="0.22" />
              <stop offset="82%" stopColor="#000" stopOpacity="0" />
              <stop offset="100%" stopColor="#fff" stopOpacity="0.14" />
            </radialGradient>
            {/* Stop colours come from CSS rather than attributes so the whole
                housing — rim, pegs, needle — re-skins with the profile's theme
                without a second copy of this SVG. */}
            <linearGradient id="pegShine" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" className={styles.pegTop} />
              <stop offset="100%" className={styles.pegBottom} />
            </linearGradient>
          </defs>

          <g>
            {slices.map((slice) => (
              <path
                key={slice.segment.id}
                d={slice.path}
                fill={slice.segment.color}
                shapeRendering="geometricPrecision"
              />
            ))}
          </g>

          {/* One overlay for the whole disc rather than a gradient per slice. */}
          <circle cx={CENTER} cy={CENTER} r={OUTER_RADIUS} fill="url(#sliceDepth)" />

          <g stroke="rgba(255,255,255,0.16)" strokeWidth="0.35">
            {slices.map((slice) => {
              const inner = polar(slice.start, INNER_RADIUS);
              const outer = polar(slice.start, OUTER_RADIUS);
              return (
                <line
                  key={`sep-${slice.segment.id}`}
                  x1={inner.x}
                  y1={inner.y}
                  x2={outer.x}
                  y2={outer.y}
                />
              );
            })}
          </g>

          <g className={styles.labels}>
            {slices.map((slice) => {
              const label = sliceLabel(slice.segment, naming);
              const scale = labelScale(slice.arc);
              const iconSize = 6.8 * scale;
              return (
                <g key={`label-${slice.segment.id}`}>
                  <g transform={slice.iconTransform}>
                    <Icon
                      name={slice.segment.icon as IconName}
                      x={-iconSize / 2}
                      y={-iconSize / 2}
                      width={iconSize}
                      height={iconSize}
                      strokeWidth={2.2}
                      className={styles.labelIcon}
                    />
                  </g>
                  <g transform={slice.labelTransform}>
                    <text
                      className={styles.labelPrimary}
                      textAnchor="middle"
                      dominantBaseline="central"
                      fontSize={fitFontSize(label.primary, PRIMARY_SIZE, PRIMARY_MIN_SIZE) * scale}
                      y={label.secondary ? -1.9 * scale : 0}
                    >
                      {label.primary}
                    </text>
                    {label.secondary && (
                      <text
                        className={styles.labelSecondary}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize={
                          fitFontSize(label.secondary, SECONDARY_SIZE, SECONDARY_MIN_SIZE) * scale
                        }
                        y={4.2 * scale}
                      >
                        {label.secondary}
                      </text>
                    )}
                  </g>
                </g>
              );
            })}
          </g>

          {/* Pegs ride the wheel — they're what the pointer clicks against. */}
          <g>
            {slices.map((slice) => {
              const point = polar(slice.start, pegRadius);
              return (
                <circle
                  key={`peg-${slice.segment.id}`}
                  cx={point.x}
                  cy={point.y}
                  r={1.15}
                  fill="url(#pegShine)"
                />
              );
            })}
          </g>
        </svg>
      </div>

      {/* Fixed lighting. Never rotates — that's the whole trick. */}
      <svg
        viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
        className={styles.overlay}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <linearGradient id="specular" x1="12%" y1="0%" x2="70%" y2="100%">
            <stop offset="0%" stopColor="#fff" stopOpacity="0.22" />
            <stop offset="34%" stopColor="#fff" stopOpacity="0.05" />
            <stop offset="58%" stopColor="#fff" stopOpacity="0" />
          </linearGradient>
          {/* Dark housing with a single light break at the top-left, so the rim
              frames the wheel instead of competing with it. */}
          <linearGradient id="rimMetal" x1="18%" y1="0%" x2="82%" y2="100%">
            <stop offset="0%" className={styles.rim1} />
            <stop offset="22%" className={styles.rim2} />
            <stop offset="52%" className={styles.rim3} />
            <stop offset="80%" className={styles.rim4} />
            <stop offset="100%" className={styles.rim5} />
          </linearGradient>
          <radialGradient id="vignette" cx="50%" cy="50%" r="50%">
            <stop offset="70%" stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.4" />
          </radialGradient>
        </defs>

        <circle cx={CENTER} cy={CENTER} r={OUTER_RADIUS} fill="url(#specular)" />
        <circle cx={CENTER} cy={CENTER} r={OUTER_RADIUS} fill="url(#vignette)" />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={OUTER_RADIUS + 2.6}
          fill="none"
          stroke="url(#rimMetal)"
          strokeWidth="3.4"
        />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={OUTER_RADIUS + 0.6}
          fill="none"
          className={styles.rimHairline}
          strokeWidth="0.4"
        />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={OUTER_RADIUS + 4.8}
          fill="none"
          stroke="rgba(0,0,0,0.55)"
          strokeWidth="1"
        />
      </svg>

      <div className={styles.pointerMount} aria-hidden="true">
        <div className={styles.pointer} ref={pointerRef}>
          <svg viewBox="0 0 24 34" className={styles.pointerSvg}>
            <defs>
              <linearGradient id="needleMetal" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" className={styles.needle1} />
                <stop offset="45%" className={styles.needle2} />
                <stop offset="55%" className={styles.needle3} />
                <stop offset="100%" className={styles.needle4} />
              </linearGradient>
            </defs>
            {/* Housing plate — straddles the rim so the needle reads as mounted
                hardware rather than an icon floating above the wheel. */}
            <rect
              x="3.6"
              y="8"
              width="16.8"
              height="9.4"
              rx="4.7"
              className={styles.needleHousing}
              stroke="rgba(255,255,255,0.14)"
              strokeWidth="0.7"
            />
            {/* Tapered needle. A wide teardrop reads as a map pin; too narrow
                and it disappears into the rim. */}
            <path d="M12 32.5 6.6 18a5.6 5.6 0 0 1 10.8 0Z" fill="url(#needleMetal)" />
            {/* Bevel down the left face — one highlight is enough to imply metal. */}
            <path d="M12 32.5 9.2 18.4a5.6 5.6 0 0 1 2.8-1.7Z" fill="#fff" fillOpacity="0.32" />
            {/* The jewel at the mounting screw. Ruby on the vampire wheel, a
                dark setting elsewhere — the one spot of colour on the needle. */}
            <circle cx="12" cy="15" r="3.6" className={styles.needleJewel} />
            <circle cx="12" cy="15" r="1.8" fill="url(#needleMetal)" />
          </svg>
        </div>
      </div>

      <button
        type="button"
        className={styles.hub}
        onClick={onSpin}
        disabled={disabled}
        aria-label={spinning ? 'Spinning' : 'Spin the wheel'}
        aria-busy={spinning}
      >
        <span className={styles.hubShimmer} aria-hidden="true" />
        <span className={styles.hubFace}>
          <Icon name={hubIcon} size={22} />
          <span className={styles.hubLabel}>{hubLabel}</span>
        </span>
      </button>
    </div>
  );
}

/** What a slice says: a headline, and an optional caption under it. */
interface SliceLabel {
  primary: string;
  secondary?: string;
}

/**
 * The reward, written onto its own slice.
 *
 * Two lines rather than one truncated string: the amount reads large and the
 * unit reads small underneath, which stays legible at a glance while the wheel
 * is moving. Anything that can't be split that way falls back to breaking the
 * name near its middle so neither line dominates.
 *
 * The unit is the creator's own word for her money, so it is a caption of
 * unknown length: `fitFontSize` steps it down instead of letting it spill, and
 * holds anything up to about fourteen characters.
 */
function sliceLabel(segment: PublicWheelSegment, naming: EconomyNaming): SliceLabel {
  switch (segment.reward) {
    case 'credits':
      return {
        primary: compactAmount(segment.value),
        secondary: creditWord(naming, segment.value),
      };
    case 'xp':
      return { primary: compactAmount(segment.value), secondary: naming.xpName };
    case 'nothing':
      return { primary: 'Try', secondary: 'again' };
    default:
      return balancedLines(segment.name);
  }
}

/** 1 500 → "1.5K". Keeps four-digit prizes from eating the whole slice. */
function compactAmount(value: number): string {
  if (value < 1000) return String(value);
  const thousands = value / 1000;
  return `${thousands.toFixed(value % 1000 === 0 ? 0 : 1)}K`;
}

/** Breaks a name at the word boundary closest to its middle. */
function balancedLines(name: string): SliceLabel {
  const trimmed = name.trim();
  if (trimmed.length <= 8 || !trimmed.includes(' ')) return { primary: trimmed };

  let best = -1;
  let bestGap = Infinity;
  for (let i = trimmed.indexOf(' '); i !== -1; i = trimmed.indexOf(' ', i + 1)) {
    const gap = Math.abs(i - (trimmed.length - i - 1));
    if (gap < bestGap) {
      bestGap = gap;
      best = i;
    }
  }

  return best === -1
    ? { primary: trimmed }
    : { primary: trimmed.slice(0, best), secondary: trimmed.slice(best + 1) };
}
